import React, {useEffect, useState} from 'react';
import {ResumeComponent} from './ResumeComponent';
import * as SchemaTypes from '../../../api/graphql/core/graphql';
import {
  FetchContextQueryVariables,
  useFetchContextQuery,
} from '../../../views/PrintPage/StoryContext.gql.generated';
import {API} from '../../../classes/API';
import {ProfileData} from '../../../types';
import {
  useProfileDetailsQuery,
  useProfileSkillsQuery,
} from '../../../views/PrintPage/Profile.gql.generated';
import {User} from '@supabase/supabase-js';
import {formatNameDutch} from '../../../utils/i18n';

interface ResumeComponentConnectProps {
  profileId: string | null;
  isForPDF?: boolean;
  user: User | null;
  onImageLoad?: (src: string) => void;
  onDOMStable?: () => void;
}

export function ResumeComponentConnect({
  profileId,
  isForPDF = false,
  user,
  onImageLoad,
  onDOMStable,
}: ResumeComponentConnectProps) {
  const api = API.getInstance();
  const [profileDataState, setProfileDataState] = useState<ProfileData | null>(
    null,
  );

  // Prepare variables and fetch contexts (always call hooks before any returns)
  let contextVariables: FetchContextQueryVariables | undefined;
  if (profileId) {
    contextVariables = {
      filter: {
        baseProfileId: {
          eq: profileId as SchemaTypes.Scalars['UUID']['input'],
        },
      },
      includeStories: true,
    } as unknown as FetchContextQueryVariables;
  }

  const {data: contextData} = useFetchContextQuery({
    variables: contextVariables,
    skip: !profileId,
  });

  const {data: profileData} = useProfileDetailsQuery({
    variables: {
      profileId: profileId as SchemaTypes.InputMaybe<
        SchemaTypes.Scalars['UUID']
      >,
    },
    skip: !profileId,
  });

  const {data: skillsData} = useProfileSkillsQuery({
    variables: {
      profileId: profileId as unknown as SchemaTypes.Scalars['UUID'],
    },
    skip: !profileId,
  });

  useEffect(() => {
    if (skillsData?.collection?.edges) {
      setProfileDataState(prev => {
        const newState = {
          skills: skillsData?.collection?.edges
            .map(edge => {
              const skill = edge.node;
              const appreciationEdges =
                skill.profileSkillAppreciationCollection?.edges || [];
              const names = appreciationEdges
                .map(e => {
                  const rater = e.node.rater as unknown as {
                    profile?: {firstName: string; lastName: string};
                    companyProfile?: {name: string};
                  };
                  const pf = rater?.profile;
                  const cp = rater?.companyProfile;
                  if (pf?.firstName || pf?.lastName) {
                    return formatNameDutch(pf?.firstName, pf?.lastName);
                  }
                  if (cp?.name) {
                    return cp.name;
                  }
                  return null;
                })
                .filter(Boolean) as string[];
              const uniqueNames = Array.from(new Set(names));
              const topTwo = uniqueNames.slice(0, 2);
              const othersCount = Math.max(
                (skill.rateCount || 0) - topTwo.length,
                0,
              );
              let appreciatedByText: string | null = null;
              if (topTwo.length > 0 || othersCount > 0) {
                const prefix = 'Gewaardeerd door ';
                if (topTwo.length === 2 && othersCount > 0) {
                  appreciatedByText = `${prefix}${topTwo[0]}, ${topTwo[1]} en ${othersCount} anderen`;
                } else if (topTwo.length === 2) {
                  appreciatedByText = `${prefix}${topTwo[0]} en ${topTwo[1]}`;
                } else if (topTwo.length === 1 && othersCount > 0) {
                  appreciatedByText = `${prefix}${topTwo[0]} en ${othersCount} anderen`;
                } else if (topTwo.length === 1) {
                  appreciatedByText = `${prefix}${topTwo[0]}`;
                } else if (othersCount > 0) {
                  appreciatedByText = `${prefix}${othersCount} anderen`;
                }
              }
              return {...skill, appreciatedByText};
            })
            .filter(skill => skill.rateAverage && skill.rateAverage > 6),
        };
        return {
          ...(prev || {}),
          ...newState,
        };
      });
    }
  }, [skillsData]);

  useEffect(() => {
    if (profileData?.profile?.edges[0]?.node) {
      const node = profileData.profile.edges[0]
        .node as unknown as ProfileData & {avatar?: {URI?: string} | string};
      const rawAvatar = (node as unknown as {avatar?: {URI?: string} | string})
        ?.avatar;
      const avatarURI =
        rawAvatar && typeof rawAvatar === 'object' && 'URI' in rawAvatar
          ? (rawAvatar as {URI?: string}).URI
          : (rawAvatar as string | undefined);
      const avatarUrl = avatarURI
        ? api.getPublicURL(avatarURI) || avatarURI
        : undefined;
      const email = String(user?.email).toLowerCase();

      setProfileDataState(prev => ({
        ...(prev || {}),
        ...(node as ProfileData),
        avatar: avatarUrl,
        email,
      }));
    }
  }, [api, profileData, user?.email]);

  useEffect(() => {
    if (!contextData?.collection?.edges) return;
    const contexts = contextData.collection.edges.map(edge => {
      const node = edge.node;
      const company = node.companyProfile?.name ?? null;
      // Aggregate media URIs from all stories under this context
      const media: string[] = [];
      const skillSet = new Set<string>();
      const skillMap = new Map<
        string,
        {name: string; rating?: number | null; count?: number | null}
      >();
      node.storyContextCollection?.edges.forEach(scEdge => {
        const story = scEdge.node.story;
        story?.mediaSet?.mediaCollection?.edges.forEach(mEdge => {
          if (mEdge.node.type !== 'image') return;
          const uri = api.getPublicURL(mEdge.node.URI) || mEdge.node.URI;
          if (uri) {
            media.push(uri);
          }
        });
        story?.storySkillCollection?.edges.forEach(sEdge => {
          const skillName = sEdge.node.skill?.name;
          if (skillName) {
            skillSet.add(skillName);
            // Keep the highest rating for each skill
            const existing = skillMap.get(skillName);
            const currentRating = sEdge.node.rateAverage;
            if (
              !existing ||
              (currentRating &&
                (!existing.rating || currentRating > existing.rating))
            ) {
              skillMap.set(skillName, {
                name: skillName,
                rating: currentRating,
                count: sEdge.node.rateCount,
              });
            }
          }
        });
      });

      return {
        id: String(node.id),
        name: node.name ?? null,
        role: node.role ?? null,
        company,
        startDate: (node.dateFrom as unknown as string) ?? null,
        endDate: (node.dateTo as unknown as string) ?? null,
        media: media.slice(0, 3),
        skills: Array.from(skillMap.values()),
        type: (node.type as unknown as string) ?? null,
        summary: (node.summary as unknown as string[]) ?? null,
      };
    });
    const workExperience = contexts?.filter(ctx => ctx.type === 'company');
    const education = contexts?.filter(ctx => ctx.type === 'education');

    setProfileDataState(prev => {
      const prevIds = (prev?.contexts ?? []).map(c => c.id).join(',');
      const newIds = contexts.map(c => c.id).join(',');
      if (prevIds === newIds) return prev;
      return {
        ...(prev || {}),
        workExperience,
        education,
      };
    });
  }, [api, contextData, setProfileDataState]);

  // Generate QR code data
  const qrLink = api.getWebsiteUrl(`/join?inviter=${profileDataState?.id}`);

  if (!profileDataState) return null;

  return (
    <ResumeComponent
      resumeData={profileDataState}
      isForPDF={isForPDF}
      qrData={qrLink || 'https://slip-app.nl/download'}
      onImageLoad={onImageLoad}
      onDOMStable={onDOMStable}
    />
  );
}
